import { withLogParticipants } from '../shared/log-participant-snapshot';
import { saveAppliedTruth } from '../shared/applied-truth-transport';
import { buildAppliedFrame, type AppliedFrameResult } from '../janus-engine/transport/applied-frame';
import { observeActionComputed } from '../janus-engine/compile/action-computed-delta';
import { gmStatusAppliedFrame } from '../janus-engine/transport/gm-applied-frame';
import { frameCopyOnRead } from '../janus-engine/ability/frame-snapshot';
import { possessorSceneContext } from './possessor-scene.js';
import { withAsyncDeadline } from '../shared/async-deadline';
import { normalizePhpListish } from '../shared/php-listish.js';
// resource-truth.ts — Lane L3 (2026-09-26): the client-truth GM / SHEET RESOURCE adapter + WS subscriber.
//
// The pure resolvers (janus-engine/resources/resource-adjust.ts) decide the change against the row the page holds;
// the row is mutated in place and rendered at once; the commit rides the actor's SHARED outbox (movement-truth.ts —
// one per-actor revision orders a walk, a Dodge and an add-AP together) to resource_truth_commit_inc.php
// after the browser pushes its applied delta. Old server-relayed frames remain readable during bundle-first delivery.
//
// N6 (2026-10-01): the legacy adjusts/*.php endpoints are GONE (R-043…R-046). Every click goes through resourceAdjust() /
// awardLuck(): the shared actor-row loader (shared/actor-row-loader.ts) gets the row — held, or its encounter fetched into the page — and
// an ENCOUNTER-LESS row (a PC sheet outside play) is decided against the row itself and saved through the row-only transport
// (encounter_id '' — the commit authorizes from the row and CASes its revision). Anything the page cannot decide is a refusal WITH WORDS.
//
// Reconciliation: a rejected / corrected operation adopts the AUTHORITATIVE values of its own columns (the answer /
// the correction frame carries them) — never a whole-row snapshot, which would erase a later committed action.
import {
  applyResourceValues, resolveResourceAdjust, RESOURCE_KIND_COLUMNS, RESOURCE_SCHEMA,
  RESOURCE_TAIL_KINDS,
  type ResourceColumn, type ResourceKind, type ResourceReaders, type ResourceValues,
} from '../janus-engine/resources/resource-adjust.js';
import {
  MovementTruthRejected, MovementTruthTerminal,
  type ActorTruthCommandBase, type MovementTruthAcceptedResult, type MovementTruthRejectedResult, type MovementTruthRow, type MovementTruthTerminalInfo,
} from '../janus-engine/movement/client-truth-commit.js';
import type { CasterArr } from '../shared/array-types.js';
import { classifyRefusal } from '../shared/revert-on-refusal.js';
import { ensureActorRowHeld, postRowOnly, runRowOnly } from '../shared/actor-row-loader.js';
import { handleCasterUpdate } from './caster-update.js';
import {
  actorEncounterFor, actorOutbox, actorRowFor, admitProvisional, bankConfirmedRev, confirmedApsFor, confirmedRevFor,
  noteFrameSeen, noteOwnOperation, pendingFor, reconcileActorRow, registerTruthSchema,
} from './movement-truth.js';
import { applyApsScalar } from '../janus-engine/movement/client-truth-commit.js';
import { resolveResourceRecovery, emptyResourceRecovery, RECOVERY_STATUS_IDS, type ResourceRecoveryPlan } from '../janus-engine/resources/resource-recovery.js';
import { ecId, ecLean, scalar, type Row as EngineRow } from '../janus-engine/ability/context.js';
import { statusItemId, statusRows } from '../janus-engine/statuses/expiry.js';
import { browserRollSource } from '../janus-engine/actions/roll-source.js';
import { abilityTruthReaders, abilityTruthCampaign, fetchObjectsBatch, hydrateTurnTriggerDefinitions } from './ability-truth.js';
import { movementScopeReaders } from './movement-truth.js';
import { planWithConsent, type PageTriggerConsent } from './trigger-consent.js';
import { fetchAndCacheCaster } from './caster-cache.js';
import { hydrateRemovalSupportDefinitions } from '../janus-engine/statuses/removal-support.js';
import { queueActorSave } from './applied-save-outbox';
import { spawnSavePending } from '../shared/spawn-save-pending';

type Row = Record<string, unknown>;
const asRow = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
const upper = (value: unknown): string => String(value ?? '').trim().toUpperCase();
const ENDPOINT = '/options/game/actions/resource_truth_commit_inc.php';

export interface ResourceTruthCommand extends ActorTruthCommandBase {
  kind: ResourceKind; roll_private: number; delta: number;
  after: ResourceValues;
  recovery?: ResourceRecoveryPlan;
}
export interface ResourceTruthFrame { values: ResourceValues; vitality?: Row; blob?: Row; recovery?: boolean }
const readers: ResourceReaders = {
  layer: (row, key) => ftLayerValue(row, key),
  hasLayer: (row, key) => { const v = ftLayerValue(row, key); return v !== null && v !== undefined; },
};
const cmd = (command: ActorTruthCommandBase): ResourceTruthCommand => command as ResourceTruthCommand;

const recoveryDue = (row: Row, kind: ResourceKind, after: ResourceValues): boolean => RESOURCE_TAIL_KINDS.has(kind)
  && ((Number(scalar(ecLean(row, 'encounter_creature_dead')) ?? 0) === 1 && Number(after.v_current) > 0)
    || statusRows(row, 'target').some(status => [...RECOVERY_STATUS_IDS, '99C862B9-D6AE-4E18-7803-A6F9B42A88E1'].includes(statusItemId(status))));

/** The ordinary scalar click stays synchronous. Only a due recovery needs definitions, consent, or a status frame. */
function recoveryPlan(row: Row, encounter: Row | null, after: ResourceValues, consent?: PageTriggerConsent) {
  const held = encounter ?? { encounter_creatures_arr: [row], encounter_inventories_arr: [] };
  const members = new Map<string, EngineRow>(normalizePhpListish(held.encounter_creatures_arr)
    .flatMap(value => { const member = asRow(value), id = member ? ecId(member) : ''; return member && id ? [[id, member] as [string, EngineRow]] : []; }));
  members.set(ecId(row), row);
  const complete = new Set([...members].filter(([, member]) => asRow(asRow(member.computed)?.creature) && asRow(member.item_layer_types)).map(([id]) => id));
  const read = { ...abilityTruthReaders(row, held, complete), geometry: movementScopeReaders(held).geometry,
    ...(consent ? { triggerConsent: consent } : {}) };
  return resolveResourceRecovery(ecId(row), after, held, members, read, browserRollSource());
}

async function prepareRecovery(row: Row, encounter: Row | null, kind: ResourceKind, after: ResourceValues): Promise<void> {
  if (!recoveryDue(row, kind, after)) return;
  const held = encounter ?? { encounter_creatures_arr: [row], encounter_inventories_arr: [] };
  await fetchObjectsBatch([...RECOVERY_STATUS_IDS, '99C862B9-D6AE-4E18-7803-A6F9B42A88E1', 'F5ACBDB4-D923-3684-57F6-FBF3F720AEFA']);
  await hydrateRemovalSupportDefinitions(statusRows(row, 'target'), abilityTruthReaders(row, held, new Set([ecId(row)])), fetchObjectsBatch);
  await hydrateTurnTriggerDefinitions(row, held, []);
}

const recoveryRefreshes = new Set<string>();
function refreshRecovery(command: ActorTruthCommandBase, frame: ResourceTruthFrame): void {
  if (!frame?.recovery || recoveryRefreshes.has(command.operation_id)) return;
  recoveryRefreshes.add(command.operation_id);
  if (recoveryRefreshes.size > 256) recoveryRefreshes.delete(recoveryRefreshes.values().next().value!);
  if (command.encounter_id) void window.ftResyncEncounter?.(command.encounter_id);
  else void fetchAndCacheCaster(command.actor_id, { skipEncounterSeed: true });
}

/** In-flight operations of this lane: the columns they changed and their values BEFORE the optimistic apply. */
interface Pending { prior: ResourceValues; cols: ResourceColumn[]; encounterId: string; actorId: string; rev: number }
const pending = new Map<string, Pending>();
const localFrames = new Map<string, AppliedFrameResult>();
const actorKey = (encounterId: string, actorId: string): string => upper(encounterId) + ':' + upper(actorId);
/** Columns a NEWER optimistic operation of ours (same actor, higher revision) rewrote: saved truth for an older revision
 * must not land on them — that operation's own answer will (Codex review 2026-09-26, TS F2/F3). */
function newerPendingCols(command: ActorTruthCommandBase, rev: number): Set<ResourceColumn> {
  const out = new Set<ResourceColumn>();
  for (const p of pending.values()) if (p.encounterId === upper(command.encounter_id) && p.actorId === upper(command.actor_id) && p.rev > rev) p.cols.forEach(c => out.add(c));
  return out;
}
/** Per-actor, PER-COLUMN confirmed truth: each resource column remembers the revision that last confirmed it, so a late
 * answer / correction never overwrites a value a newer revision already confirmed (Codex review TS F1). */
const confirmedCols = new Map<string, Partial<Record<ResourceColumn, { rev: number; value: number }>>>();
/** Bank saved values at `rev`; returns the subset that is not superseded (to install on the row). */
function bankCols(encounterId: string, actorId: string, rev: number, values: ResourceValues): ResourceValues {
  const key = actorKey(encounterId, actorId);
  const known = confirmedCols.get(key) ?? {};
  const fresh: ResourceValues = {};
  for (const [col, value] of Object.entries(values) as [ResourceColumn, number][]) {
    if (!Number.isFinite(Number(value)) || (known[col] && known[col]!.rev > rev)) continue;
    known[col] = { rev, value: Number(value) };
    fresh[col] = Number(value);
  }
  confirmedCols.set(key, known);
  if (confirmedCols.size > 512) confirmedCols.delete(confirmedCols.keys().next().value!);
  return fresh;
}
function currentValues(row: Row, cols: readonly ResourceColumn[]): ResourceValues {
  const ec = asRow(row.encounter_creatures) ?? {};
  const out: ResourceValues = {};
  for (const col of cols) { const raw = Array.isArray(ec[col]) ? (ec[col] as unknown[])[0] : ec[col]; out[col] = Math.trunc(Number(raw ?? 0)) || 0; }
  return out;
}
/** Repaint only the resource columns that changed. They default to the command's own `after`, which only an operation THIS page
 * built carries — a wire frame (frame.values), a resync and an award stub name the columns they installed. */
function render(command: ActorTruthCommandBase, row: MovementTruthRow, columns: readonly string[] = Object.keys(cmd(command).after ?? {})): void {
  const paths = columns.map(column => `encounter_creatures.${column}`);
  if (paths.some(path => /\.(v_current|v_temp|v_max|v_max_temp)$/.test(path))) paths.push('computed.vitality');
  if (cmd(command).recovery) paths.push('statuses');
  handleCasterUpdate({ type: 'caster_update', caster_arr: row as unknown as CasterArr, local: true }, paths);
  const applied_frame = localFrames.get(command.operation_id);
  if (applied_frame) {
    localFrames.delete(command.operation_id);
    recoveryRefreshes.add(command.operation_id);
    if (recoveryRefreshes.size > 256) recoveryRefreshes.delete(recoveryRefreshes.values().next().value!);
    window.ftPushAppliedTruth?.({ type: 'janus_resource_truth', command: { ...command, rev: applied_frame.rev,
      frame: { values: cmd(command).after } }, applied_frame });
  }
  window.dispatchEvent(new CustomEvent('ft:resource-truth-applied', { detail: { command } }));
}
/** Install saved columns at `rev` unless superseded or owned by a newer pending operation. */
function installSaved(command: ActorTruthCommandBase, row: Row, rev: number, frame: ResourceTruthFrame): void {
  const fresh = bankCols(command.encounter_id, command.actor_id, rev, frame.values ?? {});
  const blocked = newerPendingCols(command, rev);
  const values: ResourceValues = {};
  const queuedRestOverlay = cmd(command).kind === 'ooc_rest' && pendingFor(command.encounter_id, command.actor_id) > 0
    && Number(asRow(row.encounter_creatures)?.movement_rev ?? 0) > rev;
  for (const [col, value] of Object.entries(fresh) as [ResourceColumn, number][]) {
    // HTTP answers and publish-first committed/correction frames must leave a newer
    // queued walk's AP on screen. Saved AP is still banked above and by the shared lane.
    if (!blocked.has(col) && !(col === 'aps' && queuedRestOverlay)) values[col] = value;
  }
  applyResourceValues(row, values, readers, null);
  const blob = asRow(frame.blob);
  if (blob && 'mana' in values) {
    const ec = (row.encounter_creatures ??= {}) as Row;
    for (const key of ['mana', 'mana_percent']) { if (blob[key] === undefined) delete ec[key]; else ec[key] = blob[key]; }
  }
  refreshRecovery(command, frame);
}
/** AP is the one resource the shared confirmed snapshot tracks (movement + A1 + this lane): bank it from saved truth. */
const apsScalar = (values: ResourceValues): { aps?: number } => Number.isInteger(values.aps) ? { aps: values.aps } : {};
function restoreSharedScalars(command: ActorTruthCommandBase, row: MovementTruthRow): void {
  if (cmd(command).kind !== 'ooc_rest' || pendingFor(command.encounter_id, command.actor_id) === 0) {
    applyApsScalar(row.encounter_creatures, confirmedApsFor(command.encounter_id, command.actor_id, row));
  }
  reconcileActorRow(command.encounter_id, command.actor_id, row);
}

/** POST with a 20 s budget for the ANSWER HEADERS only. An AbortSignal.timeout on the whole fetch also aborts the BODY
 * read, so a busy main thread that reads a committed answer late turned a saved commit into a failure (smoke
 * 2026-09-26: the answer arrived in 6.7 s, the page read it at 26 s). Once headers arrive the body is read in full. */
async function postJson(body: { operation_id: string }): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(new DOMException('signal timed out', 'TimeoutError')), 20000);
  try { return await saveAppliedTruth(ENDPOINT, body, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body), signal: controller.signal }); }
  finally { clearTimeout(timer); }
}
async function post(command: ActorTruthCommandBase): Promise<unknown> {
  const response = await postJson(withLogParticipants(command));
  let result: Row | null = null;
  try { result = await withAsyncDeadline(() => response.json() as Promise<Row>, 180000); } catch { result = null; }
  // A rest is an idempotent background pin. A competing rest may already have saved it;
  // reconcile as done, without treating every later actor command as a rejected chain.
  const values = asRow(result?.values);
  if (response.status === 409 && result?.error === 'stale' && Number.isInteger(result.rev) && cmd(command).kind === 'ooc_rest'
    && values && RESOURCE_KIND_COLUMNS.ooc_rest.every(col => Number.isInteger(values[col]) && values[col] === cmd(command).after[col])) {
    return { ok: true, applied: false, alreadyRested: true, rev: Number(result.rev), frame: { values } };
  }
  if (!response.ok && result?.error === 'stale' && Number.isInteger(result.rev)) {
    const row = actorRowFor(command.encounter_id, command.actor_id);
    const position = { x_pos: Number(row?.position?.x_pos ?? 0), y_pos: Number(row?.position?.y_pos ?? 0), z_pos: Number(row?.position?.z_pos ?? 0) };
    throw new MovementTruthRejected({ ok: false, error: 'stale', rev: Number(result.rev), position, values: asRow(result.values) ?? {} } as unknown as MovementTruthRejectedResult);
  }
  if (response.ok && result?.ok === true) return result;
  const message = String(result?.error ?? (response.statusText || 'Resource delivery failed'));
  if (response.status >= 400 && response.status < 500) throw new MovementTruthTerminal(response.status, message);
  throw new Error(message);
}
function accepted(command: ActorTruthCommandBase, result: MovementTruthAcceptedResult): void {
  const row = actorRowFor(command.encounter_id, command.actor_id);
  pending.delete(command.operation_id);
  if (!row) return;
  const saved = asRow((result as unknown as Row).frame) as ResourceTruthFrame | null;
  bankConfirmedRev(command.encounter_id, command.actor_id, row, result.rev, apsScalar(saved?.values ?? {}));
  if (saved) installSaved(command, row as Row, result.rev, saved);
  if (pendingFor(command.encounter_id, command.actor_id) === 0) restoreSharedScalars(command, row);
  render(command, row);
  if (cmd(command).kind === 'ooc_rest') window.dispatchEvent(new CustomEvent('ft:ooc-rest-settled', { detail: { command, rev: result.rev, done: true } }));
}
/** Put an operation's own columns back to CONFIRMED truth (else its prior value), skipping columns a newer pending
 * operation of ours owns and columns already handled (Codex review TS F2). */
function restoreOwn(command: ActorTruthCommandBase, row: Row, own: Pending, handled: ResourceColumn[] = []): void {
  const blocked = newerPendingCols(command, own.rev);
  const known = confirmedCols.get(actorKey(command.encounter_id, command.actor_id)) ?? {};
  const values: ResourceValues = {};
  for (const col of own.cols) if (!blocked.has(col) && !handled.includes(col)) values[col] = known[col]?.value ?? own.prior[col];
  if (Object.keys(values).length) applyResourceValues(row, values, readers);
}
function rejected(command: ActorTruthCommandBase, result: MovementTruthRejectedResult): void {
  const row = actorRowFor(command.encounter_id, command.actor_id);
  const own = pending.get(command.operation_id);
  pending.delete(command.operation_id);
  if (row) {
    const values = (asRow((result as unknown as Row).values) ?? {}) as ResourceValues;
    bankConfirmedRev(command.encounter_id, command.actor_id, row, result.rev, apsScalar(values));
    installSaved(command, row as Row, result.rev, { values });
    if (own) restoreOwn(command, row as Row, own, Object.keys(values) as ResourceColumn[]);
    restoreSharedScalars(command, row);
    render(command, row);
  }
  window.dispatchEvent(new CustomEvent('ft:resource-truth-reconciled', { detail: { command, authoritative: { rev: result.rev } } }));
  if (cmd(command).kind === 'ooc_rest') window.dispatchEvent(new CustomEvent('ft:ooc-rest-settled', { detail: { command, rev: result.rev, done: false } }));
}
function failed(command: ActorTruthCommandBase, info: MovementTruthTerminalInfo): void {
  const row = actorRowFor(command.encounter_id, command.actor_id);
  const own = pending.get(command.operation_id);
  pending.delete(command.operation_id);
  if (row) {
    if (own) restoreOwn(command, row as Row, own);
    restoreSharedScalars(command, row);
    render(command, row);
  }
  // R-124: the out-of-combat rest is the page's own background decision, not a click — its refusal is reported, never toasted.
  if (cmd(command).kind !== 'ooc_rest' && typeof displayWarning === 'function') displayWarning(info.status === 403 ? 'You do not have permission to adjust this creature.' : 'The adjustment was refused: ' + info.error);
  window.dispatchEvent(new CustomEvent('ft:resource-truth-refused', { detail: { command, info, refusal: classifyRefusal({ status: info.status, body: { error: info.error } }) } }));
}
registerTruthSchema(RESOURCE_SCHEMA, {
  apply: (row, command, rev) => {
    const c = cmd(command);
    const cols = [...RESOURCE_KIND_COLUMNS[c.kind]];
    const prior = currentValues(row as Row, cols);
    // First sight of a column: the row's value IS confirmed truth at the confirmed revision.
    const key = actorKey(command.encounter_id, command.actor_id);
    const known = confirmedCols.get(key) ?? {};
    const base = confirmedRevFor(command.encounter_id, command.actor_id, row);
    for (const col of cols) known[col] ??= { rev: base, value: prior[col]! };
    confirmedCols.set(key, known);
    pending.set(command.operation_id, { prior, cols, encounterId: upper(command.encounter_id), actorId: upper(command.actor_id), rev });
    if (pending.size > 256) pending.delete(pending.keys().next().value!);
    const identity = { operation_id: command.operation_id, encounter_id: command.encounter_id, actor_id: command.actor_id,
      rev, member_revs: { [command.actor_id]: rev } };
    const encounter = actorEncounterFor(command.encounter_id) as Row;
    let recoveryFrame: AppliedFrameResult | undefined;
    if (c.recovery && (Object.values(c.recovery.verdict).some(value => Array.isArray(value) ? value.length : Object.keys(value ?? {}).length)
      || c.recovery.bodies.length || c.recovery.monsterBitRemovals.length)) {
      const roster = new Map(normalizePhpListish(encounter.encounter_creatures_arr).map(value => [ecId(value as Row), value as Row]));
      roster.set(command.actor_id, row as Row);
      const shadow = frameCopyOnRead(encounter, roster, { catalogs: 'share' });
      shadow.encounter.encounter_creatures_arr = [...shadow.members.values()];
      const verdict = { ...c.recovery.verdict, ec: { ...c.recovery.verdict.ec,
        [command.actor_id]: { ...c.recovery.verdict.ec[command.actor_id], ...c.after } }, recovery: c.recovery };
      recoveryFrame = gmStatusAppliedFrame(identity, verdict, shadow.encounter, abilityTruthCampaign(encounter),
        abilityTruthReaders(row as Row, encounter, new Set([command.actor_id])).definition,
        (member, roster) => possessorSceneContext(encounter, member, roster));
      if (!('cache_ref' in recoveryFrame)) {
        recoveryFrame.rows = recoveryFrame.rows.flatMap(entry => {
          if (entry.table !== 'encounter_creatures') return [entry];
          const changes = verdict.ec[entry.row_id];
          return changes ? [{ ...entry, partial: true, row: { ...changes, encounter_creature_id: entry.row_id,
            movement_rev: recoveryFrame!.member_revs[entry.row_id] } }] : [];
        });
        const { member_catalogs: _heldCatalogs, ...inputs } = recoveryFrame.inputs ?? {};
        recoveryFrame.inputs = inputs;
      }
    }
    const finish = observeActionComputed([[command.actor_id, row as Row]], new Map([[command.actor_id, {
      computed: [...(cols.some(col => col.startsWith('v_')) ? [['vitality']] : []),
        ...(c.kind === 'weapon_attack' ? [['creature', 'combat', 'hindered_attack_counter']] : [])],
    }]]));
    applyResourceValues(row as Row, c.after, readers);
    row.encounter_creatures.movement_rev = rev;
    const computed_delta = finish();
    localFrames.set(command.operation_id, recoveryFrame ?? buildAppliedFrame({ ...identity,
      rows: [{ table: 'encounter_creatures', row_id: command.actor_id, partial: true,
        row: { ...c.after, encounter_creature_id: command.actor_id, movement_rev: rev } }],
      computed: {}, computed_delta, new_entities: [], deletes: [] }, Infinity));
  },
  render, commit: post, accepted, rejected, failed,
  staleMessage: 'Your adjustment was rejected: this creature changed elsewhere. Its current values were restored.',
});

/** The encounter holding the actor: the page's active encounter first, then any cached encounter. */
function encounterIdFor(actorId: string, hint?: string): string | null {
  const ids = [hint, window.ftGetActiveEncounterId?.()].map(upper).filter(Boolean);
  for (const id of ids) if (actorRowFor(id, actorId)) return id;
  const cache = (window as unknown as { FateTide?: { encounterCache?: { keys?: () => Iterable<string> } } }).FateTide?.encounterCache;
  for (const key of cache?.keys?.() ?? []) if (actorRowFor(upper(key), actorId)) return upper(key);
  return null;
}

export type ResourceCommitOutcome = 'handled' | 'refused' | null;
/**
 * N6e (2026-10-02, owner rule: PHP = permission gate + lazy save): the turn-counter rules resource_truth_commit_inc.php used to re-judge
 * (`add_ap adds exactly one`, `Reaction already available`, `Attack adjust must move with the delta`) are checked HERE, on every command
 * before it is queued or posted (the resolver decides them; this refuses a command that breaks them, whatever built it). The PHP checks are
 * deleted. Null = the command keeps the rules; else the words.
 */
function turnCounterRule(kind: ResourceKind, before: ResourceValues, after: ResourceValues, delta: number): string | null {
  // N6f (coordinator Codex Round 10 B4b, P3): every value the rule compares must be a real integer — a missing / NaN `before` made both
  // comparisons false, so a NEGATIVE attack adjust passed (`(now > NaN) === (-1 > 0)`); the deleted PHP compared ints only.
  const int = (v: unknown): v is number => typeof v === 'number' && Number.isInteger(v);
  switch (kind) {
    case 'add_ap': return int(before.aps) && int(after.aps) && after.aps === before.aps + 1 ? null : 'AP was not changed: adding AP adds exactly one.';
    case 'add_reaction': return before.reaction === 1 ? 'You already have your reaction!' : after.reaction === 1 ? null : 'The reaction was not changed: adding it makes it available.';
    case 'weapon_attack': case 'head_attack': {
      const was = before[kind], now = after[kind];
      return int(was) && int(now) && int(delta) && now !== was && delta !== 0 && (now > was) === (delta > 0) ? null : `${KIND_NOUN[kind]} were not changed: the count must move with the adjustment.`;
    }
    default: return null;
  }
}
/** Null = required state is unavailable before mutation. The public seam loads it or refuses with words;
 * 'handled' means the browser decision was queued on the actor outbox. */
export function tryCommitResource(input: { kind: ResourceKind; actorId: string; encounterId?: string; delta?: number; rollPrivate?: number; consent?: PageTriggerConsent }): ResourceCommitOutcome {
  const actorId = upper(input.actorId);
  const kind = input.kind;
  if (!RESOURCE_KIND_COLUMNS[kind] || !actorId) return null;
  const encounterId = encounterIdFor(actorId, input.encounterId);
  if (!encounterId || !actorEncounterFor(encounterId)) return null;
  const row = actorRowFor(encounterId, actorId);
  if (!row) return null;
  if (spawnSavePending(encounterId, actorId, row)) { lastHandbackReason = 'spawn-save-pending'; return null; }
  confirmedRevFor(encounterId, actorId, row);
  const resolution = resolveResourceAdjust(row as Row, { kind, delta: input.delta }, readers);
  if (!resolution.supported) {
    if (resolution.refused) { if (typeof displayWarning === 'function') displayWarning(resolution.message); return 'refused'; }
    lastHandbackReason = resolution.reason;
    window.dispatchEvent(new CustomEvent('ft:resource-slice-unavailable', { detail: { kind, reason: resolution.reason } }));
    return null;
  }
  const broken = turnCounterRule(kind, resolution.before as ResourceValues, resolution.after, resolution.delta);
  if (broken) { if (typeof displayWarning === 'function') displayWarning(broken); return 'refused'; }
  let recovery = RESOURCE_TAIL_KINDS.has(kind) ? emptyResourceRecovery() : undefined;
  if (recoveryDue(row as Row, kind, resolution.after)) {
    const result = recoveryPlan(row as Row, actorEncounterFor(encounterId) as Row | null, resolution.after, input.consent);
    if (result.kind !== 'resolved') { lastHandbackReason = result.reason; return null; }
    recovery = result.value;
    resolution.after.v_current = Number(recovery.verdict.ec[actorId]?.v_current ?? resolution.after.v_current);
    delete recovery.verdict.ec[actorId]?.v_current;
    if (recovery.verdict.ec[actorId] && !Object.keys(recovery.verdict.ec[actorId]!).length) delete recovery.verdict.ec[actorId];
  }
  const rollPrivate = [0, 1, 2].includes(Number(input.rollPrivate)) ? Number(input.rollPrivate) : 0;
  const command: ResourceTruthCommand = {
    schema: RESOURCE_SCHEMA, operation_id: crypto.randomUUID().toUpperCase(), encounter_id: encounterId, actor_id: actorId,
    expected_rev: Number(row.encounter_creatures.movement_rev ?? 0), kind, roll_private: rollPrivate, delta: resolution.delta,
    before: resolution.before as ActorTruthCommandBase['before'], after: resolution.after,
    ...(recovery ? { recovery } : {}),
  };
  try {
    actorOutbox(encounterId, actorId).enqueue(withLogParticipants(command));
  } catch (error) {
    const own = pending.get(command.operation_id);
    pending.delete(command.operation_id);
    if (own) { applyResourceValues(row as Row, own.prior, readers); row.encounter_creatures.movement_rev = command.expected_rev; render(command, row); }
    if (typeof displayWarning === 'function') displayWarning('The adjustment could not be committed: ' + (error instanceof Error ? error.message : String(error)));
    return 'refused';
  }
  noteOwnOperation(command.operation_id, RESOURCE_SCHEMA);
  return 'handled';
}

/**
 * R-124 (2026-10-01): post a change the page ALREADY DECIDED elsewhere (the out-of-combat rest — janus-engine/economy/out-of-combat.ts)
 * on the actor's shared outbox, exactly like a resolved click: optimistic apply → resource_truth_commit_inc.php (CAS on `before` + the
 * per-actor revision) → frames. Null before any mutation when the page does not hold the row or the row no longer holds `before`.
 */
export function tryCommitResolvedResource(input: { kind: ResourceKind; encounterId: string; actorId: string; before: ResourceValues; after: ResourceValues; delta?: number }): ResourceCommitOutcome | 'unchanged' {
  const encounterId = upper(input.encounterId), actorId = upper(input.actorId), kind = input.kind;
  const cols = RESOURCE_KIND_COLUMNS[kind];
  if (!cols || !encounterId || !actorId || !actorEncounterFor(encounterId)) return null;
  const row = actorRowFor(encounterId, actorId);
  if (!row || spawnSavePending(encounterId, actorId, row)) return null;
  const now = currentValues(row as Row, cols);
  if (cols.some(col => now[col] !== input.before[col] || !Number.isInteger(input.after[col]))) return null;
  if (kind === 'ooc_rest' && cols.every(col => now[col] === input.after[col])) return 'unchanged';
  if (turnCounterRule(kind, input.before, input.after, Math.trunc(Number(input.delta ?? 0)) || 0) !== null) return null; // N6e: the rules PHP no longer re-judges
  confirmedRevFor(encounterId, actorId, row);
  const pick = (values: ResourceValues): ResourceValues => Object.fromEntries(cols.map(col => [col, values[col]])) as ResourceValues;
  const command: ResourceTruthCommand = {
    schema: RESOURCE_SCHEMA, operation_id: crypto.randomUUID().toUpperCase(), encounter_id: encounterId, actor_id: actorId,
    expected_rev: Number(row.encounter_creatures.movement_rev ?? 0), kind, roll_private: 0, delta: Math.trunc(Number(input.delta ?? 0)) || 0,
    before: pick(input.before) as ActorTruthCommandBase['before'], after: pick(input.after),
  };
  try {
    actorOutbox(encounterId, actorId).enqueue(withLogParticipants(command));
  } catch (error) {
    const own = pending.get(command.operation_id);
    pending.delete(command.operation_id);
    if (own) { applyResourceValues(row as Row, own.prior, readers); row.encounter_creatures.movement_rev = command.expected_rev; render(command, row); }
    window.dispatchEvent(new CustomEvent('ft:resource-truth-refused', { detail: { command, info: { status: 0, error: error instanceof Error ? error.message : String(error) } } }));
    return 'refused';
  }
  noteOwnOperation(command.operation_id, RESOURCE_SCHEMA);
  return 'handled';
}

type ResourceTruthWireFrame = ActorTruthCommandBase & { kind: ResourceKind | 'award_luck'; rev: number; frame: ResourceTruthFrame; targets?: { actor_id: string; expected_rev: number; after: { luck: number } }[]; provisional?: boolean; committed?: boolean; correction?: boolean };
/** Publish-first subscriber: provisional frames render but never bank; committed frames advance confirmed and install
 * the saved scalar values; a correction installs the saved values of the kind's columns. */
export function handleResourceTruth(command: ResourceTruthWireFrame): void {
  if (command?.schema === RESOURCE_SCHEMA && command.kind === 'award_luck' && command.committed && command.targets) {
    for (const target of command.targets) handleResourceTruth({ ...command, operation_id: command.operation_id + '/' + target.actor_id,
      actor_id: target.actor_id, expected_rev: target.expected_rev, kind: 'luck', rev: target.expected_rev + 1, frame: { values: target.after } });
    return;
  }
  if (command?.schema !== RESOURCE_SCHEMA || !Number.isInteger(command.rev) || command.rev < 0) return;
  const kind = command.correction ? 'correction' : command.committed ? 'committed' : 'provisional';
  const row = actorRowFor(command.encounter_id, command.actor_id);
  const frame = asRow(command.frame) as ResourceTruthFrame | null;
  if (!row || !frame || !asRow(frame.values)) return;
  if (!noteFrameSeen(command.operation_id, kind)) return;
  if (kind === 'provisional') {
    // The operation id arms the shared resync watch (MV3): a lost committed frame is recovered from the saved row.
    if (!admitProvisional(command.encounter_id, command.actor_id, row, command.rev, command.operation_id)) return;
    applyResourceValues(row as Row, frame.values, readers);
    render(command, row, Object.keys(frame.values));
    return;
  }
  bankConfirmedRev(command.encounter_id, command.actor_id, row, command.rev, apsScalar(frame.values));
  pending.delete(command.operation_id);
  // Committed AND correction frames go through the per-column revision gate (a late correction cannot overwrite newer truth).
  installSaved(command, row as Row, command.rev, frame);
  if (pendingFor(command.encounter_id, command.actor_id) === 0) restoreSharedScalars(command, row);
  render(command, row, Object.keys(frame.values));
  window.dispatchEvent(new CustomEvent('ft:resource-truth-frame', { detail: { kind, command } }));
}

/** What the resolver handed back for, in the last null tryCommitResource() — the seam words it. */
let lastHandbackReason = '';
const KIND_NOUN: Record<ResourceKind, string> = {
  add_ap: 'AP', add_reaction: 'The reaction', weapon_attack: 'Weapon attacks', head_attack: 'Head attacks', luck: 'Luck', mana: 'Mana',
  hunger: 'Hunger', thirst: 'Thirst', temp_v: 'Temporary vitality', max_temp_v: 'Temporary max vitality', max_v: 'Max vitality', v: 'Vitality',
  heal_to_max: 'Vitality', ooc_rest: 'AP',
};
function refuse(message: string): 'refused' {
  if (typeof displayWarning === 'function') displayWarning(message);
  window.dispatchEvent(new CustomEvent('ft:resource-truth-refused-words', { detail: { message } }));
  return 'refused';
}
function handbackWords(kind: ResourceKind, reason: string): string {
  const noun = KIND_NOUN[kind] ?? 'The value';
  if (reason === 'aps-ceiling') return `${noun} was not changed: it is already at its maximum (127).`;
  if (reason === 'delta-not-numeric') return `${noun} was not changed: enter a whole number.`;
  if (reason === 'spawn-save-pending') return `${noun} was not changed: this creature is still being added. Try again once it is saved.`;
  return `${noun} was not changed: this adjustment is not available here.`;
}

/** ROW-ONLY (an encounter-less row — a PC sheet outside play): resolve against the row itself, post to the same commit with
 * encounter_id '', install the saved answer. Runs inside the actor's admission chain (resourceAdjust → runRowOnly), so a second click
 * resolves against the row the first answer left. No optimistic draw: the row changes when the save answers. */
/** N6c (owner 2026-10-01): on an encounter-less row only the SHEET EDITS stay (V, heal, temp V, max V, luck, mana, hunger, thirst). The
 *  turn-economy counters — AP, the reaction, the weapon / head attack counters — exist only inside an encounter's turns: refused, never posted. */
const ENCOUNTER_ONLY_KINDS: ReadonlySet<ResourceKind> = new Set<ResourceKind>(['add_ap', 'add_reaction', 'weapon_attack', 'head_attack']);
async function commitResourceRowOnly(kind: ResourceKind, where: { actorId: string; row: Row }, delta: number | undefined, rollPrivate: number): Promise<ResourceCommitOutcome> {
  const noun = KIND_NOUN[kind] ?? 'The value';
  if (ENCOUNTER_ONLY_KINDS.has(kind)) return refuse(`${noun} ${kind === 'add_ap' || kind === 'add_reaction' ? 'was' : 'were'} not changed: a creature outside an encounter has no turns. Bring it into an encounter first.`);
  {
    const row = where.row;
    const resolution = resolveResourceAdjust(row, { kind, delta }, readers);
    if (!resolution.supported) return resolution.refused ? refuse(resolution.message) : refuse(handbackWords(kind, resolution.reason));
    const broken = turnCounterRule(kind, resolution.before as ResourceValues, resolution.after, resolution.delta);
    if (broken) return refuse(broken);
    try { await prepareRecovery(row, null, kind, resolution.after); }
    catch { return refuse(handbackWords(kind, 'unavailable')); }
    let recovery = RESOURCE_TAIL_KINDS.has(kind) ? emptyResourceRecovery() : undefined;
    if (recoveryDue(row, kind, resolution.after)) {
      const plan = await planWithConsent('', where.actorId, consent => recoveryPlan(row, null, resolution.after, consent), result => result.kind === 'handback' && result.reason.includes('trigger-consent'));
      if (plan.kind !== 'resolved') return refuse(handbackWords(kind, plan.reason));
      recovery = plan.value;
      resolution.after.v_current = Number(recovery.verdict.ec[where.actorId]?.v_current ?? resolution.after.v_current);
      delete recovery.verdict.ec[where.actorId]?.v_current;
      if (recovery.verdict.ec[where.actorId] && !Object.keys(recovery.verdict.ec[where.actorId]!).length) delete recovery.verdict.ec[where.actorId];
    }
    const ec = (row.encounter_creatures ??= {}) as Row;
    const expectedRev = Math.trunc(Number(Array.isArray(ec.movement_rev) ? ec.movement_rev[0] : ec.movement_rev ?? 0)) || 0;
    const body = { schema: RESOURCE_SCHEMA, operation_id: crypto.randomUUID().toUpperCase(), encounter_id: '', actor_id: where.actorId,
      expected_rev: expectedRev, kind, roll_private: rollPrivate, delta: resolution.delta, before: resolution.before, after: resolution.after,
      ...(recovery ? { recovery } : {}) };
    const answer = await postRowOnly(ENDPOINT, withLogParticipants(body, [row]));
    const result = answer.body;
    const frame = asRow(result?.frame);
    const install = (values: unknown, rev: unknown): void => {
      // Never over a NEWER row (a caster_update that landed while this answer was in flight — Codex N6 review).
      const held = Math.trunc(Number(Array.isArray(ec.movement_rev) ? ec.movement_rev[0] : ec.movement_rev ?? 0)) || 0;
      if (Number.isInteger(rev) && held > (rev as number)) return;
      const v = asRow(values);
      if (v) applyResourceValues(row, v as ResourceValues, readers, null);
      if (Number.isInteger(rev)) ec.movement_rev = rev as number;
      render(body as unknown as ActorTruthCommandBase, row as unknown as MovementTruthRow, Object.keys(v ?? {}));
    };
    if (answer.status >= 200 && answer.status < 300 && result?.ok === true) {
      install(frame?.values, result.rev);
      refreshRecovery(body as unknown as ActorTruthCommandBase, frame as unknown as ResourceTruthFrame);
      window.dispatchEvent(new CustomEvent('ft:resource-truth-row-only', { detail: { body, result } }));
      return 'handled';
    }
    const refusal = classifyRefusal({ status: answer.status, body: result, thrown: answer.status === 0 });
    if (refusal?.reason === 'stale') {
      install(asRow(result?.values) ?? asRow(asRow(refusal.authoritative)?.values), result?.rev ?? refusal.rev);
      return refuse(`${noun} was not changed: it changed elsewhere. The current value is shown — try again.`);
    }
    if (refusal?.reason === 'permission') return refuse(`${noun} was not changed: you do not have permission to change this character.`);
    if (answer.status === 0) return refuse(`${noun} could not be confirmed — check it before trying again.`);
    return refuse(`${noun} was not changed (${String(result?.error ?? answer.status)}).`);
  }
}

/** The ONE caller seam (info panels, creature actions, the console): read the delta from `deltaInputId` (or `opts.delta`), get the
 * actor's row through the shared loader and commit — the encounter outbox for a row in an encounter, the row-only save for an
 * encounter-less one. Never a legacy post: what the page cannot decide is refused with words. */
export async function resourceAdjust(kind: ResourceKind, actorId: string, deltaInputId: string | null,
  opts: { encounterId?: string; rollPrivate?: number; delta?: number } = {}): Promise<ResourceCommitOutcome> {
  const noun = KIND_NOUN[kind] ?? 'The value';
  if (!RESOURCE_KIND_COLUMNS[kind] || kind === 'ooc_rest') return refuse(`${noun} was not changed: this adjustment is not available here.`);
  let delta = opts.delta;
  if (deltaInputId) {
    const raw = (document.getElementById(deltaInputId) as HTMLInputElement | null)?.value ?? '';
    delta = /^\s*-?\d+\s*$/.test(raw) ? parseInt(raw, 10) : undefined;
    if (delta === undefined) return refuse(`${noun} was not changed: enter a whole number.`);
  }
  const rollPrivate = [0, 1, 2].includes(Number(opts.rollPrivate)) ? Number(opts.rollPrivate) : 0;
  // Admission in CLICK order, per actor, from before the first await (Codex N6 review P2): a second click waits for the first to be
  // loaded, decided and queued (or answered, row-only) — never overtaken by a faster fetch.
  return runRowOnly(actorId, async (): Promise<ResourceCommitOutcome> => {
    const where = await ensureActorRowHeld(actorId, opts.encounterId, { allowRowOnly: true, what: noun });
    if (where.kind === 'refused') return refuse(where.message);
    if (where.kind === 'row') return commitResourceRowOnly(kind, where, delta, rollPrivate);
    const row = actorRowFor(where.encounterId, where.actorId)!;
    const resolution = resolveResourceAdjust(row as Row, { kind, delta }, readers);
    try { if (resolution.supported) await prepareRecovery(row as Row, actorEncounterFor(where.encounterId) as Row | null, kind, resolution.after); }
    catch { return refuse(handbackWords(kind, 'unavailable')); }
    lastHandbackReason = '';
    const outcome = await planWithConsent(where.encounterId, where.actorId,
      consent => tryCommitResource({ kind, actorId: where.actorId, encounterId: where.encounterId, delta, rollPrivate, consent }),
      result => result === null && lastHandbackReason.includes('trigger-consent'));
    return outcome === null ? refuse(handbackWords(kind, lastHandbackReason)) : outcome;
  });
}
/** Info-panel form: `ftResourceAdjust('mana', ecId, 'input_mana_qty')` — the roll privacy from the page's encounter context. */
function ftResourceAdjust(kind: ResourceKind, actorId: string, deltaInputId: string | null): Promise<ResourceCommitOutcome> {
  const w = window as unknown as { ftGetEncounterContext?: () => { roll_private?: unknown } };
  const rollPrivate = Number(w.ftGetEncounterContext?.()?.roll_private ?? 0) || 0;
  return resourceAdjust(kind, actorId, deltaInputId, { rollPrivate });
}

/** GM Award Luck through the loader: every target's encounter is loaded when the page lacks it (award luck is an ENCOUNTER GM tool —
 * an encounter-less row is refused), then ONE all-or-nothing commit. Never a legacy post. */
export async function awardLuck(input: { targetIds: string[]; encounterId?: string; rollPrivate?: number }): Promise<ResourceCommitOutcome> {
  const ids = [...new Set((input.targetIds ?? []).map(upper).filter(Boolean))];
  if (!ids.length) return refuse('Award luck: pick at least one creature.');
  if (ids.length > 64) return refuse('Award luck: pick at most 64 creatures at once.');
  let encounterId = upper(input.encounterId);
  for (const id of ids) {
    const where = await ensureActorRowHeld(id, encounterId || undefined, { what: 'Luck' });
    if (where.kind === 'refused') return refuse(where.message);
    if (where.kind !== 'encounter') return refuse('Luck was not changed: award luck needs the creatures in an encounter.');
    if (encounterId && where.encounterId !== encounterId) return refuse('Luck was not changed: the creatures are in different encounters — award them one encounter at a time.');
    encounterId = where.encounterId;
  }
  const outcome = await tryCommitAwardLuck({ ...input, targetIds: ids, encounterId });
  return outcome === null ? refuse('Luck was not changed: a creature is still saving another change — try again in a moment.') : outcome;
}

/**
 * S2 — GM AWARD LUCK on N rows (gm_award_luck_inc.php): +1 luck each (the adjust_luck clamp), ONE all-or-nothing commit.
 * Multi-actor: reserve every target's outbox and send one all-or-nothing save. Null means a target is missing;
 * the public seam reports it with words. 'handled' means applied optimistically and posted.
 */
export async function tryCommitAwardLuck(input: { targetIds: string[]; encounterId?: string; rollPrivate?: number }): Promise<ResourceCommitOutcome> {
  const ids = [...new Set((input.targetIds ?? []).map(upper).filter(Boolean))];
  if (!ids.length || ids.length > 64) return null;
  const encounterId = encounterIdFor(ids[0]!, input.encounterId);
  if (!encounterId) return null;
  const rows = ids.map(id => actorRowFor(encounterId, id));
  if (rows.some((row, i) => !row || spawnSavePending(encounterId, ids[i]!, row))) return null;
  const targets: { actor_id: string; expected_rev: number; before: { luck: number }; after: { luck: number } }[] = [];
  for (const [i, id] of ids.entries()) {
    const row = rows[i]!;
    confirmedRevFor(encounterId, id, row);
    const res = resolveResourceAdjust(row as Row, { kind: 'luck', delta: 1 }, readers);
    if (!res.supported) return null;
    targets.push({ actor_id: id, expected_rev: Number(row.encounter_creatures.movement_rev ?? 0), before: { luck: res.before.luck ?? 0 }, after: { luck: res.after.luck ?? 0 } });
  }
  const oid = crypto.randomUUID().toUpperCase();
  const stub = (id: string): ActorTruthCommandBase => ({ schema: RESOURCE_SCHEMA, operation_id: oid + '/' + id, encounter_id: encounterId, actor_id: id, expected_rev: 0, before: {} });
  targets.forEach((t, i) => {
    const row = rows[i]!;
    applyResourceValues(row as Row, { luck: t.after.luck }, readers);
    row.encounter_creatures.movement_rev = t.expected_rev + 1;
    noteOwnOperation(oid + '/' + t.actor_id, RESOURCE_SCHEMA);
    pending.set(oid + '/' + t.actor_id, { prior: { luck: t.before.luck }, cols: ['luck'], encounterId, actorId: t.actor_id, rev: t.expected_rev + 1 });
    render(stub(t.actor_id), row, ['luck']);
  });
  const settle = (answer: Record<string, { rev: number; luck: number }> | null, fallback: boolean): void => {
    targets.forEach(t => pending.delete(oid + '/' + t.actor_id));
    targets.forEach((t, i) => {
      const row = actorRowFor(encounterId, t.actor_id) ?? rows[i]!;
      const saved = answer?.[t.actor_id];
      if (saved && Number.isInteger(saved.rev)) {
        bankConfirmedRev(encounterId, t.actor_id, row, saved.rev);
        installSaved(stub(t.actor_id), row as Row, saved.rev, { values: { luck: Number(saved.luck) } });
      } else if (fallback) {
        const known = confirmedCols.get(actorKey(encounterId, t.actor_id))?.luck;
        applyResourceValues(row as Row, { luck: known?.value ?? t.before.luck }, readers);
        row.encounter_creatures.movement_rev = t.expected_rev;
      }
      restoreSharedScalars(stub(t.actor_id), row);
      render(stub(t.actor_id), row, ['luck']);
    });
  };
  const body = withLogParticipants({ schema: RESOURCE_SCHEMA, operation_id: oid, encounter_id: encounterId, actor_id: targets[0]!.actor_id, expected_rev: targets[0]!.expected_rev,
    kind: 'award_luck', roll_private: [0, 1, 2].includes(Number(input.rollPrivate)) ? Number(input.rollPrivate) : 0, targets });
  window.ftPushAppliedTruth?.({ type: 'janus_resource_truth', command: body, applied_frame: buildAppliedFrame({
    operation_id: oid, encounter_id: encounterId, actor_id: body.actor_id, rev: body.expected_rev + 1,
    member_revs: Object.fromEntries(targets.map(target => [target.actor_id, target.expected_rev + 1])),
    rows: targets.map(target => ({ table: 'encounter_creatures', row_id: target.actor_id, partial: true,
      row: { encounter_creature_id: target.actor_id, luck: target.after.luck, movement_rev: target.expected_rev + 1 } })),
    computed: {}, new_entities: [], deletes: [] }, Infinity) });
  queueActorSave({ encounterId, actorIds: ids, command: body,
    send: async () => {
      const response = await postJson(body);
      const result = asRow(await response.json().catch(() => null));
      if (response.status >= 500 || (response.ok && !result)) throw new Error(String(result?.error ?? 'Award luck save unavailable'));
      const answer = asRow(result?.targets) as Record<string, { rev: number; luck: number }> | null;
      if (response.ok && result?.ok === true) {
        targets.forEach(t => pending.delete(oid + '/' + t.actor_id));
        // Later actions already read the local award. Bank its receipt without repainting
        // their luck or collapsing their optimistic revision.
        targets.forEach((t, i) => {
          const saved = answer?.[t.actor_id];
          if (saved) {
            bankConfirmedRev(encounterId, t.actor_id, rows[i]!, saved.rev);
            bankCols(encounterId, t.actor_id, saved.rev, { luck: saved.luck });
          }
        });
        return { ok: true };
      }
      return { ok: false, status: response.status, error: String(result?.error ?? 'Award luck refused'), targets: answer };
    },
    refused: async info => {
      if (info.chained) { targets.forEach(t => pending.delete(oid + '/' + t.actor_id)); return; }
      settle(asRow(info.body?.targets) as Record<string, { rev: number; luck: number }> | null, true);
      if (typeof displayWarning === 'function') displayWarning(info.status === 409 ? 'Award luck was rejected: a creature changed elsewhere. Current luck restored.'
        : info.status === 403 ? 'Only the campaign GM can award luck.' : 'Award luck failed: ' + info.error);
      await window.ftResyncEncounter?.(encounterId);
    },
  });
  return 'handled';
}

/** MV3 follow-up (2026-09-28): the shared resync read (movement-truth.ts — armed by ANY schema's peer provisional that is
 * not followed by its committed / correction frame) carries the actor's saved resource columns. Install them exactly as a
 * committed frame would: per-column revision gate, never over a newer pending operation of ours. A NULL mana (never set)
 * is left to the next frame / snapshot (its default is the compiled spirit layer, not a column value). */
const RESOURCE_COLUMNS = new Set<string>(Object.values(RESOURCE_KIND_COLUMNS).flat());
window.addEventListener('ft:actor-truth-resync', event => {
  const detail = (event as CustomEvent).detail as { encounter_id?: string; actor_id?: string; rev?: number; values?: Record<string, unknown>; deferred?: boolean } | null;
  if (!detail?.encounter_id || !detail.actor_id || !Number.isInteger(detail.rev)) return;
  const row = actorRowFor(detail.encounter_id, detail.actor_id);
  if (!row) return;
  // Never over newer truth from ANY schema (the resource column map only knows this lane's installs — Codex MV3 follow-up
  // finding 1): skip when the actor's shared confirmed revision is already past the read, or any of our work is pending.
  // A skip is DEFERRED, never dropped (Codex MV3 closing review, P3): the watch reads again later instead of resolving.
  if (confirmedRevFor(detail.encounter_id, detail.actor_id, row) > detail.rev! || pendingFor(detail.encounter_id, detail.actor_id) > 0) { detail.deferred = true; return; }
  const values: ResourceValues = {};
  for (const [col, value] of Object.entries(detail.values ?? {})) {
    if (RESOURCE_COLUMNS.has(col) && value !== null && value !== undefined && Number.isFinite(Number(value))) values[col as ResourceColumn] = Math.trunc(Number(value));
  }
  if (!Object.keys(values).length) return;
  const stub: ActorTruthCommandBase = { schema: RESOURCE_SCHEMA, operation_id: 'resync', encounter_id: detail.encounter_id, actor_id: detail.actor_id, expected_rev: detail.rev!, before: {} };
  installSaved(stub, row as Row, detail.rev!, { values });
  if (pendingFor(detail.encounter_id, detail.actor_id) === 0) restoreSharedScalars(stub, row);
  render(stub, row, Object.keys(values));
});

Object.assign(window, { ftTryCommitResource: tryCommitResource, ftResourceAdjustTruth: resourceAdjust, ftResourceAdjust, ftTryCommitAwardLuck: tryCommitAwardLuck, ftAwardLuck: awardLuck });
